/**
 * 文件/视频发送纯逻辑单点（任务 14.3b）：附件分流（图片/视频/文件）+ MIME 推导。
 * 主线程入口（useImageSender 附件路径）与 Worker 发送链共用，保证「选了什么 →
 * 发什么 msgType → envelope 标什么 mime」一个口径。
 *
 * 判定口径对齐旧版 MediaFileService（isImageFileName/isVideoFileName/
 * mimeFromFileName——全按文件名扩展名）；在此之上补浏览器事实兜底：扩展名
 * 判不出时看 File.type 前缀（旧版 Flutter 无此信息，Web 有就用，分流更准）。
 * 大小上限与图片/语音同一道门（uploadv2 对所有文件统一 5MB，uploadsUtils.go
 * MaxUploadSize 实锤非图片专属）；上传不压缩不改后缀（后端非图片原样存）。
 */

const IMAGE_EXT_RE = /\.(jpg|jpeg|png|gif|webp|heic)$/;
const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm|avi|mkv)$/;

/** 附件类别（附件入口/拖拽的分流结果；image 走既有图片管线） */
export type AttachmentKind = 'image' | 'video' | 'file';

/** 旧版 mimeFromFileName 同表（兜底 octet-stream） */
const MIME_BY_EXT: Readonly<Record<string, string>> = {
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  heic: 'image/heic',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  m4v: 'video/x-m4v',
  webm: 'video/webm',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  wav: 'audio/wav',
  mp3: 'audio/mpeg',
  pdf: 'application/pdf',
  txt: 'text/plain',
};

/**
 * 附件分流：扩展名优先（旧版口径），判不出再看浏览器给的 MIME 前缀。
 * image → 既有图片管线（压缩/测量/相册），video → msgType 7，其余 → msgType 8。
 */
export function attachmentKindFor(fileName: string, blobType: string): AttachmentKind {
  const lower = fileName.toLowerCase();
  if (IMAGE_EXT_RE.test(lower)) return 'image';
  if (VIDEO_EXT_RE.test(lower)) return 'video';
  if (blobType.startsWith('image/')) return 'image';
  if (blobType.startsWith('video/')) return 'video';
  return 'file';
}

/** envelope 的 mime 字段：浏览器事实优先，缺失按扩展名表推，再兜底 octet-stream */
export function attachmentMimeFor(fileName: string, blobType: string): string {
  if (blobType !== '') return blobType;
  const dot = fileName.lastIndexOf('.');
  const ext = dot >= 0 ? fileName.slice(dot + 1).toLowerCase() : '';
  return MIME_BY_EXT[ext] ?? 'application/octet-stream';
}
